import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// A impressora é escolhida na tela inicial da bancada, logo depois do crachá
// (aqui o campo de bipe puxa o foco e um <select> seria impossível de operar);
// este hook só a lê.
import { SendZplToPrinter, useZplPrinters } from '../warehouse/zplPrinting';
import { useWarehouseLabels } from '../warehouse/warehouseLabels';
import { useKiosk } from '../warehouse/collector/KioskContext';
import ScanArmedBox from '../warehouse/collector/ScanArmedBox';
import { Package, Tag } from 'shared/icons';
import Button from '../ui/Button';
import { Mono, Sku } from '../ui/Mono';
import { Skeleton } from '../ui/Skeleton';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';

// ─────────────────────────────────────────────────────────────────────────────
// EMBALAR ENVIOS — a bancada de packing (monitor 1280×1024), no ritmo que a
// operação real provou: bipa o PRODUTO → EMBALA (a janela conta NO SERVIDOR;
// bipar antes da hora REINICIA o tempo) → a etiqueta sai SOZINHA no fim da
// contagem → bipa a ETIQUETA impressa → pacote fechado, próximo.
//
// Uma ação por tela, número gigante, erro em vermelho com o que fazer — o
// operador nunca decide nada além de bipar o que a tela pede.
//
// No QUIOSQUE (/bancada) o terminal só BIPA (ordem do dono, 26/09): o campo do
// bipe fica oculto (continua focado e o Enter do leitor faz a ação da etapa),
// sem botão "Bipar", e a tela mostra a caixa de "Aguardando o bipe…" do
// coletor. No console (/warehouse/bench) o campo visível continua como era.
// ─────────────────────────────────────────────────────────────────────────────

const beep = (ok) => {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = ok ? 880 : 220;
        gain.gain.value = 0.08;
        osc.start();
        osc.stop(ctx.currentTime + (ok ? 0.12 : 0.4));
    } catch {
        // sem áudio, sem drama
    }
};

const baixarZpl = (zpl, shipmentId) => {
    const blob = new Blob([zpl], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `etiqueta-${shipmentId}.zpl`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

const EnvioPackingBench = ({ operator }) => {
    // Textos do seletor de impressora e da caixa do bipe vêm do dicionário do
    // armazém — passar a chave crua fazia a tela mostrar "wms.labels.printer".
    const { label: rotulo } = useWarehouseLabels();
    const quiosque = useKiosk();
    const operatorCode = operator?.employee_code;
    // A impressora da bancada: rede cadastrada ou USB local (QZ Tray). A
    // escolha fica guardada enquanto a bancada estiver aberta — o operador
    // escolhe uma vez no começo do turno, não a cada pacote.
    const { printers, selectedPrinter } = useZplPrinters(true);
    const inputRef = useRef(null);
    const emitindoRef = useRef(false);
    const [carregando, setCarregando] = useState(true);
    const [posto, setPosto] = useState(null); // {shipment_id, sku, titulo, status}
    const [fimDaJanela, setFimDaJanela] = useState(null); // timestamp local
    const [agora, setAgora] = useState(Date.now());
    const [fila, setFila] = useState(null);
    const [codigo, setCodigo] = useState('');
    const [aviso, setAviso] = useState(null); // {tom:'ok'|'erro'|'punido', texto}
    const [ocupado, setOcupado] = useState(false);

    const focar = () => setTimeout(() => inputRef.current?.focus({ preventScroll: true }), 60);

    const aplicarPosto = useCallback((novoPosto) => {
        setPosto(novoPosto);
        if (novoPosto?.status === 'EMBALANDO') {
            setFimDaJanela(Date.now() + Number(novoPosto.espere_ms ?? 0));
        } else {
            setFimDaJanela(null);
        }
    }, []);

    const carregarEstado = useCallback(async () => {
        if (!operatorCode) return;
        try {
            const estado = await api.getColetasPackingEstado(operatorCode);
            aplicarPosto(estado.posto);
            setFila(estado.fila);
        } catch (error) {
            setAviso({ tom: 'erro', texto: error?.message || 'Não foi possível carregar a bancada.' });
        } finally {
            setCarregando(false);
            focar();
        }
    }, [operatorCode, aplicarPosto]);

    useEffect(() => {
        carregarEstado();
    }, [carregarEstado]);

    // Relógio da janela: timestamp, não contador — re-render não perde o tempo.
    useEffect(() => {
        if (!fimDaJanela) return undefined;
        const timer = setInterval(() => setAgora(Date.now()), 150);
        return () => clearInterval(timer);
    }, [fimDaJanela]);

    /**
     * Manda a etiqueta para a IMPRESSORA. Se não houver impressora escolhida —
     * ou se ela recusar —, cai para o download do .zpl: com o pacote embalado
     * na mão, ficar sem saída nenhuma é pior do que salvar o arquivo.
     */
    const enviarParaImpressora = useCallback(
        async (zpl, shipmentId) => {
            if (!zpl) {
                return;
            }
            if (!selectedPrinter) {
                baixarZpl(zpl, shipmentId);
                setAviso({
                    tom: 'erro',
                    texto: 'Nenhuma impressora escolhida — a etiqueta foi baixada. Deslogue e escolha a impressora na tela inicial da bancada.',
                });
                return null;
            }
            try {
                // Devolve o NOME para o aviso dizer para onde a etiqueta foi: a
                // mesa precisa saber em qual impressora ir buscar.
                return await SendZplToPrinter({ target: selectedPrinter, printers, zpl, label: rotulo });
            } catch (erro) {
                baixarZpl(zpl, shipmentId);
                setAviso({
                    tom: 'erro',
                    texto: `Impressora recusou (${erro?.message || 'erro'}) — a etiqueta foi baixada.`,
                });
                return null;
            }
        },
        [printers, rotulo, selectedPrinter],
    );

    const restanteMs = fimDaJanela ? Math.max(0, fimDaJanela - agora) : 0;

    const emitir = useCallback(async () => {
        if (emitindoRef.current) return;
        emitindoRef.current = true;
        try {
            const resposta = await api.postColetasPackingEmitir({ operator_code: operatorCode });
            const impressora = await enviarParaImpressora(resposta.zpl, resposta.shipment_id);
            beep(true);
            if (impressora !== null) {
                setAviso({
                    tom: 'ok',
                    texto: `Etiqueta emitida${impressora ? ` — ${impressora}` : ''}. Cole no pacote e bipe o código dela.`,
                });
            }
            await carregarEstado();
        } catch (error) {
            if (error?.details?.espere_ms) {
                setFimDaJanela(Date.now() + Number(error.details.espere_ms));
                setAviso({ tom: 'punido', texto: error.message });
                beep(false);
            } else {
                setAviso({ tom: 'erro', texto: error?.message || 'Falha ao emitir a etiqueta.' });
                beep(false);
                await carregarEstado();
            }
        } finally {
            emitindoRef.current = false;
            focar();
        }
    }, [operatorCode, carregarEstado, enviarParaImpressora]);

    // Fim da contagem → a etiqueta sai SOZINHA (com rede de segurança de 5s de
    // polling implícita: qualquer recarga da tela reconsulta o estado).
    useEffect(() => {
        if (posto?.status === 'EMBALANDO' && fimDaJanela && restanteMs === 0) {
            emitir();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [restanteMs === 0, posto?.status]);

    const bipar = async () => {
        const bruto = codigo.trim();
        setCodigo('');
        if (!bruto || ocupado) {
            focar();
            return;
        }
        setOcupado(true);
        setAviso(null);
        try {
            if (posto?.status === 'ETIQUETA_EMITIDA') {
                const resposta = await api.postColetasPackingBiparEtiqueta({
                    operator_code: operatorCode,
                    codigo: bruto,
                });
                beep(true);
                setAviso({
                    tom: 'ok',
                    texto: `Pacote ${resposta.shipment_id} FECHADO${
                        resposta.ledger ? ` — saldo ${resposta.ledger.de} → ${resposta.ledger.para}` : ''
                    }. Bipe o próximo produto.`,
                });
                aplicarPosto(null);
                await carregarEstado();
            } else {
                const novoPosto = await api.postColetasPackingBipar({
                    operator_code: operatorCode,
                    codigo: bruto,
                });
                beep(true);
                aplicarPosto({ ...novoPosto, status: 'EMBALANDO' });
                setAviso(null);
            }
        } catch (error) {
            if (error?.details?.espere_ms) {
                setFimDaJanela(Date.now() + Number(error.details.espere_ms));
                if (posto?.status !== 'EMBALANDO' && error?.details?.shipment_id) {
                    await carregarEstado();
                }
                setAviso({ tom: 'punido', texto: error.message });
            } else {
                setAviso({ tom: 'erro', texto: error?.message || 'Bip recusado.' });
            }
            beep(false);
        } finally {
            setOcupado(false);
            focar();
        }
    };

    const reimprimir = async () => {
        if (!posto?.shipment_id) return;
        try {
            const zpl = await api.getColetasZpl(posto.shipment_id);
            const nome = await enviarParaImpressora(zpl, posto.shipment_id);
            // Sem nome = já avisou o erro lá dentro; não sobrescrever com "ok".
            if (nome !== null) {
                setAviso({
                    tom: 'ok',
                    texto: `Etiqueta reimpressa${nome ? ` — ${nome}` : ''}. Descarte a anterior.`,
                });
            }
        } catch (error) {
            setAviso({ tom: 'erro', texto: error?.message || 'Não foi possível reimprimir.' });
        }
        focar();
    };

    const etapa = useMemo(() => {
        if (carregando) return 'carregando';
        if (!posto) return 'produto';
        if (posto.status === 'EMBALANDO') return 'embalando';
        return 'etiqueta';
    }, [carregando, posto]);

    const segundos = Math.ceil(restanteMs / 1000);
    const progresso = posto?.janela_ms ? Math.min(1, Math.max(0, 1 - restanteMs / posto.janela_ms)) : 0;
    const punido = aviso?.tom === 'punido';

    if (etapa === 'carregando') {
        return <Skeleton className="h-96 w-full" />;
    }

    return (
        <div className="mx-auto w-full max-w-5xl select-none space-y-4">
            {/* Aviso fixo FORA do cartão re-renderizado */}
            {aviso ? (
                <button
                    type="button"
                    onClick={() => setAviso(null)}
                    className={`block w-full rounded-xl px-4 py-3 text-left text-base font-semibold ${
                        aviso.tom === 'ok'
                            ? 'bg-success-soft text-success-text dark:bg-success-900/30'
                            : 'animate-pulse bg-danger-soft text-danger-text dark:bg-danger-900/30'
                    }`}
                >
                    {aviso.texto}
                </button>
            ) : null}

            {/* O CARTÃO da etapa — uma ação por tela */}
            {etapa === 'produto' ? (
                <div className="rounded-3xl border-4 border-info-300 bg-info-soft p-12 text-center dark:border-info-800 dark:bg-info-900/20">
                    <Package size={112} weight="duotone" className="mx-auto text-black dark:text-white" aria-hidden />
                    <h2 className="mt-4 text-5xl font-black text-black dark:text-white">BIPE O PRODUTO</h2>
                    <p className="mt-3 text-lg text-mono-600 dark:text-mono-300">
                        EAN do produto, SKU ou o número do envio
                    </p>
                </div>
            ) : null}

            {etapa === 'embalando' ? (
                <div
                    className={`rounded-3xl border-4 p-10 text-center ${
                        punido
                            ? 'border-danger-500 bg-danger-soft dark:bg-danger-900/20'
                            : 'border-mono-300 bg-white dark:border-mono-700 dark:bg-mono-950'
                    }`}
                >
                    <div className="mx-auto h-52 w-52">
                        <svg viewBox="0 0 120 120" className="h-full w-full -rotate-90">
                            <circle cx="60" cy="60" r="52" fill="none" strokeWidth="10" className="stroke-mono-200 dark:stroke-mono-800" />
                            <circle
                                cx="60"
                                cy="60"
                                r="52"
                                fill="none"
                                strokeWidth="10"
                                strokeLinecap="round"
                                strokeDasharray={2 * Math.PI * 52}
                                strokeDashoffset={2 * Math.PI * 52 * (1 - progresso)}
                                className={punido ? 'stroke-danger-500' : 'stroke-black dark:stroke-white'}
                            />
                        </svg>
                        <p className="-mt-36 font-mono text-7xl font-black tabular-nums text-black dark:text-white">
                            {segundos}
                        </p>
                    </div>
                    <h2 className="mt-16 text-4xl font-black text-black dark:text-white">EMBALE O PRODUTO</h2>
                    {punido ? (
                        <p className="mt-2 animate-bounce text-xl font-bold text-danger-text">
                            LEITURA ANTECIPADA — o tempo RECOMEÇOU!
                        </p>
                    ) : (
                        <p className="mt-2 text-lg text-mono-600 dark:text-mono-300">
                            A etiqueta será impressa quando o tempo terminar
                        </p>
                    )}
                    <div className="mt-6 space-y-1">
                        <p className="text-sm text-mono-500">
                            <Mono>{posto?.shipment_id}</Mono>
                        </p>
                        <p className="truncate text-xl font-semibold text-mono-700 dark:text-mono-300">
                            {posto?.titulo || ''}
                        </p>
                        {posto?.sku ? <Sku value={posto.sku} className="text-sm" /> : null}
                    </div>
                </div>
            ) : null}

            {etapa === 'etiqueta' ? (
                <div className="rounded-3xl border-4 border-warning-400 bg-warning-soft p-12 text-center dark:border-warning-800 dark:bg-warning-900/20">
                    <Tag size={112} weight="duotone" className="mx-auto text-black dark:text-white" aria-hidden />
                    <h2 className="mt-4 text-5xl font-black text-black dark:text-white">BIPE A ETIQUETA</h2>
                    <p className="mt-3 text-lg text-mono-700 dark:text-mono-300">
                        Cole a etiqueta impressa no pacote e bipe o código de barras dela
                    </p>
                    <div className="mt-6 space-y-1">
                        <p className="font-mono text-3xl font-black tabular-nums text-black dark:text-white">
                            {posto?.shipment_id}
                        </p>
                        <p className="truncate text-lg font-semibold text-mono-700 dark:text-mono-300">
                            {posto?.titulo || ''}
                        </p>
                    </div>
                    {/* Reimprimir VISÍVEL: escondido, o operador trava com uma
                        etiqueta ruim na mão achando que não tem saída. */}
                    <Button variant="secondary" size="lg" className="mt-6" onClick={reimprimir}>
                        <Tag size={22} className="mr-2" />
                        Reimprimir etiqueta
                    </Button>
                </div>
            ) : null}

            {/* O BIP — um input só, Enter faz a ação da etapa. No quiosque ele
                fica OCULTO (mesmo foco, mesmo Enter) e a caixa tracejada é o
                sinal de vida; no console, o campo visível + "Bipar". */}
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-mono-500">
                        {etapa === 'etiqueta' ? 'Bipe a ETIQUETA do envio' : 'Bipe o PRODUTO'}
                    </label>
                    <input
                        ref={inputRef}
                        value={codigo}
                        onChange={(evento) => setCodigo(evento.target.value)}
                        onKeyDown={(evento) => evento.key === 'Enter' && bipar()}
                        onBlur={focar}
                        autoFocus
                        disabled={ocupado}
                        inputMode="none"
                        autoComplete="off"
                        {...(quiosque
                            ? {
                                  // Sem `aria-hidden`: esconder de leitor de tela
                                  // um campo que recebe o foco é armadilha.
                                  'aria-label': rotulo('wms.collector.hid_input_aria'),
                                  autoCorrect: 'off',
                                  spellCheck: false,
                                  className: 'pointer-events-none absolute h-px w-px opacity-0',
                              }
                            : {
                                  placeholder: 'aguardando leitura…',
                                  className:
                                      'w-full rounded-xl border-2 border-mono-300 bg-white px-4 py-4 text-center font-mono text-2xl text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white',
                              })}
                    />
                    {quiosque ? <ScanArmedBox armed={!ocupado} buffer={codigo} label={rotulo} /> : null}
                </div>
                {quiosque ? null : (
                    <Button variant="primary" size="lg" onClick={bipar} disabled={ocupado || !codigo.trim()}>
                        Bipar
                    </Button>
                )}
            </div>

            {/* Fila da bancada */}
            <div className="flex flex-wrap justify-center gap-6 text-sm text-mono-500 dark:text-mono-400">
                <span>
                    Aguardando embalagem: <strong>{FormatInt(fila?.aguardando_embalagem ?? 0)}</strong>
                </span>
                <span>
                    Embalados hoje: <strong className="text-success-text">{FormatInt(fila?.embalados_hoje ?? 0)}</strong>
                </span>
            </div>
        </div>
    );
};

export default EnvioPackingBench;
